import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Calendar, AlertCircle, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { getScorecards, ScorecardMetadata } from '../services/scorecardService';
import IndexWorkspaceShell from '@/components/index/IndexWorkspaceShell';
import { formatScriptId } from '@/utils/formatIndexId';
import { echoScoreDisplay } from '@/config/indexThresholds';
import {
  recommendationFromEngagement,
  recommendationTextClass,
} from '@/config/recommendation';

const ScorecardsList: React.FC = () => {
  const navigate = useNavigate();
  const [scorecards, setScorecards] = useState<ScorecardMetadata[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const limit = 20;

  useEffect(() => {
    loadScorecards();
  }, [offset]);

  const loadScorecards = async () => {
    try {
      setLoading(true);
      setError(null);
      const results = await getScorecards(limit, offset);
      if (offset === 0) {
        setScorecards(results);
      } else {
        setScorecards(prev => [...prev, ...results]);
      }
      setHasMore(results.length === limit);
    } catch (err: any) {
      console.error('Error loading scorecards:', err);
      setError(err.message || 'Failed to load scorecards');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateValue: any): string => {
    if (!dateValue) return 'Unknown date';
    try {
      // Handle Firestore timestamp
      if (dateValue.toDate) {
        return dateValue.toDate().toLocaleDateString();
      }
      // Handle string date
      if (typeof dateValue === 'string') {
        return new Date(dateValue).toLocaleDateString();
      }
      return 'Unknown date';
    } catch {
      return 'Unknown date';
    }
  };

  const handleViewScorecard = (scorecardId: string) => {
    navigate(`/scorecards/${scorecardId}`);
  };

  const loadMore = () => {
    setOffset(prev => prev + limit);
  };

  if (loading && scorecards.length === 0) {
    return (
      <IndexWorkspaceShell activeNav="reports">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-index-accent border-t-transparent" />
        </div>
      </IndexWorkspaceShell>
    );
  }

  return (
    <IndexWorkspaceShell activeNav="reports">
      <div className="mx-auto max-w-6xl space-y-6">
        <header>
          <p className="ni-label mb-2">Reports</p>
          <h1 className="text-2xl md:text-3xl font-sans font-bold text-white flex items-center gap-3">
            <FileText className="h-7 w-7 text-index-accent shrink-0" />
            Nuclius Index reports
          </h1>
          <p className="text-white/45 mt-2 text-sm font-sans">
            Audited screenplays and forensic scorecards in your workspace.
          </p>
        </header>

        {/* Error Message */}
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {scorecards.length === 0 && !loading ? (
          <div className="ni-panel p-12 text-center">
            <FileText className="h-16 w-16 mx-auto mb-4 text-white/25" />
            <h3 className="text-lg font-sans font-semibold mb-2 text-white">No reports found</h3>
            <p className="text-white/45 mb-4 text-sm font-sans">
              No audited screenplays yet. Ingest a screenplay via NucliusAudit to begin.
            </p>
            <Button variant="indexPrimary" onClick={() => navigate('/workflow')}>
              Open NucliusAudit
            </Button>
          </div>
        ) : (
          <div className="ni-panel overflow-hidden">
            <table className="w-full text-left text-sm font-sans">
              <thead>
                <tr className="border-b border-[#2a2a2a]">
                  <th className="ni-label px-4 py-3">SCR-ID</th>
                  <th className="ni-label px-4 py-3">Title</th>
                  <th className="ni-label px-4 py-3 hidden md:table-cell">Date</th>
                  <th className="ni-label px-4 py-3">Nuclius Index</th>
                  <th className="ni-label px-4 py-3">Recommendation</th>
                  <th className="w-8" />
                </tr>
              </thead>
              <tbody>
                {scorecards.map((scorecard) => {
                  const overall = scorecard.analysis_metadata?.overall_score;
                  const meta = scorecard.analysis_metadata;
                  const echo = echoScoreDisplay(overall);
                  const engagement =
                    meta?.engagement_percentage ??
                    echo;
                  const storedLabel = meta?.recommendation_label;
                  const label =
                    (typeof storedLabel === 'string' && storedLabel) ||
                    recommendationFromEngagement(engagement) ||
                    '—';
                  return (
                    <tr
                      key={scorecard.id}
                      className="ni-table-row border-b border-[#2a2a2a] last:border-0 cursor-pointer hover:bg-white/[0.02]"
                      onClick={() => handleViewScorecard(scorecard.id)}
                    >
                      <td className="px-4 py-3 font-mono text-xs text-white/55">
                        {formatScriptId(scorecard.id)}
                      </td>
                      <td className="px-4 py-3 text-white">
                        {scorecard.script_title || 'Untitled screenplay'}
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell text-white/45 text-xs">
                        <span className="inline-flex items-center gap-1">
                          <Calendar className="h-3.5 w-3.5" />
                          {formatDate(scorecard.created_at)}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-mono text-index-accent">
                        {echo != null ? echo.toFixed(1) : '—'}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={cn(
                            'text-[10px] font-mono uppercase',
                            recommendationTextClass(label),
                          )}
                        >
                          {label}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <ChevronRight className="h-4 w-4 text-white/30" />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {hasMore && !loading && scorecards.length > 0 && (
          <div className="flex justify-center">
            <Button variant="indexOutline" onClick={loadMore}>
              Load more
            </Button>
          </div>
        )}

        {loading && scorecards.length > 0 && (
          <div className="flex justify-center">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-index-accent border-t-transparent" />
          </div>
        )}

        <div className="flex justify-end">
          <Button variant="indexPrimary" onClick={() => navigate('/workflow')}>
            Run new audit
          </Button>
        </div>
      </div>
    </IndexWorkspaceShell>
  );
};

export default ScorecardsList;
